import { zodResolver } from '@hookform/resolvers/zod'
import { type RefObject, useEffect } from 'react'
import {
	FormProvider,
	type SubmitHandler,
	useFieldArray,
	useForm
} from 'react-hook-form'
import { CharCounter } from '@/components/commons/char-counter'
import { InputText } from '@/components/commons/input-text'
import { TextArea } from '@/components/commons/textarea'
import { ButtonLegacy } from '@/components/ui/button-legacy'
import {
	ArrowRightIcon,
	PlusIcon,
	Stars01Icon,
	Trash01Icon,
	User01Icon
} from '@/components/ui/icons'
import { Separator } from '@/components/ui/separator'
import { ServiceVettingMerchFormSkeleton } from '@/features/service-vetting/components/service-vetting-merch-form-skeleton'
import { cn } from '@/helpers/utils'
import { useTranslation } from '@/translations/i18n'
import { OpsVettingMerchFormHeader } from '../../ops-vetting/components/ops-vetting-merch-form-header'
import {
	EXPERIENCE_OUTPUT_CHARACTER_LIMITS,
	ExperienceShellMerchOutputSchema
} from '../form-schemas'

interface ExpShellMerchOutputFormProps {
	isTraining?: boolean
	isOriginalContent?: boolean
	isLoading?: boolean
	isReadOnly?: boolean
	isHostEdits?: boolean
	defaultValues?: ExperienceShellMerchOutputSchema
	finalSubmitRef: RefObject<HTMLButtonElement | null>
	handleSaveCb?: (data: ExperienceShellMerchOutputSchema) => void
	handleFinalSubmissionCb?: SubmitHandler<ExperienceShellMerchOutputSchema>
}

export const ExpShellMerchOutputForm = ({
	isTraining = false,
	isOriginalContent = false,
	isLoading = false,
	isReadOnly = false,
	isHostEdits = false,
	defaultValues,
	finalSubmitRef,
	handleSaveCb,
	handleFinalSubmissionCb
}: ExpShellMerchOutputFormProps) => {
	const { t } = useTranslation()
	const methods = useForm({
		resolver:
			isTraining && !isOriginalContent
				? zodResolver(ExperienceShellMerchOutputSchema)
				: undefined,
		defaultValues
	})
	const {
		register,
		control,
		reset,
		getValues,
		formState: { errors }
	} = methods
	const { fields, remove, append } = useFieldArray({
		control,
		name: 'activities'
	})

	useEffect(() => {
		if (defaultValues) {
			reset(defaultValues)
		}
	}, [defaultValues, reset])

	return (
		<FormProvider {...methods}>
			<form
				className={cn(
					'h-full flex-1 p-6',
					isOriginalContent ? 'bg-neutral-white' : 'bg-neutral-50'
				)}
				onSubmit={methods.handleSubmit(
					handleFinalSubmissionCb ??
						(() => {
							console.log('no submission')
						})
				)}
			>
				{!isOriginalContent && (
					<OpsVettingMerchFormHeader
						description={t(
							'vetting:merch.experience.output_shell_form_description'
						)}
						endSlot={
							isTraining ? undefined : (
								<ButtonLegacy
									onClick={
										handleSaveCb &&
										(() =>
											handleSaveCb(
												getValues() as ExperienceShellMerchOutputSchema
											))
									}
									type='button'
									variant='secondary'
								>
									{t('common:save')}
								</ButtonLegacy>
							)
						}
						icon={<Stars01Icon />}
						title={t(
							'vetting:merch.experience.output_shell_form_title'
						)}
						variant={'blue'}
					/>
				)}
				{isOriginalContent && (
					<OpsVettingMerchFormHeader
						description={
							isHostEdits
								? t(
										'vetting:merch.experience.output_vestting_edits_description'
									)
								: t(
										'vetting:merch.experience.input_vetting_form_description'
									)
						}
						icon={isHostEdits ? <ArrowRightIcon /> : <User01Icon />}
						title={
							isHostEdits
								? t(
										'vetting:merch.experience.output_vestting_edits_title'
									)
								: t(
										'vetting:merch.experience.input_vetting_form_title'
									)
						}
						variant={'base'}
					/>
				)}

				{isLoading ? (
					<ServiceVettingMerchFormSkeleton />
				) : (
					<div className='flex w-full flex-col gap-2'>
						<div className='flex w-full flex-col gap-5 py-6 text-neutral-900'>
							<h4 className='font-medium text-base'>
								{t('common:experience')}
							</h4>
							<div className='flex w-full flex-col'>
								<div className='flex w-full flex-col'>
									<InputText
										disabled={isReadOnly}
										label={t(
											'vetting:merch.experience.listing_title_label'
										)}
										type={'text'}
										{...register('experience_title')}
									/>
									<CharCounter<ExperienceShellMerchOutputSchema>
										isDisabled={isOriginalContent}
										maxLength={
											EXPERIENCE_OUTPUT_CHARACTER_LIMITS
												.EXPERIENCE.TITLE
										}
										name='experience_title'
									/>
								</div>
							</div>

							<div className='flex w-full flex-col'>
								<TextArea
									disabled={isReadOnly}
									label={t(
										'vetting:merch.experience.listing_description_label'
									)}
									rows={3}
									{...register('experience_description')}
									errorMessage={
										errors?.experience_description?.message
									}
								/>
								<CharCounter<ExperienceShellMerchOutputSchema>
									isDisabled={isOriginalContent}
									maxLength={
										EXPERIENCE_OUTPUT_CHARACTER_LIMITS
											.EXPERIENCE.DESCRIPTION
									}
									name='experience_description'
								/>
							</div>

							{fields.map((activity, index) => (
								<div
									className='flex w-full flex-col gap-3'
									key={activity.id}
								>
									<div className='flex items-center justify-between gap-4'>
										<p className='font-medium text-sm'>
											{t(
												'vetting:merch.experience.activity_input_label',
												{
													index: index + 1
												}
											)}
										</p>
										{!isOriginalContent &&
											fields.length > 1 && (
												<ButtonLegacy
													onClick={() =>
														remove(index)
													}
													size='xs'
													type='button'
													variant='tertiary'
												>
													<Trash01Icon />
												</ButtonLegacy>
											)}
									</div>

									<div className='flex w-full flex-col'>
										<InputText
											disabled={isReadOnly}
											label={t(
												'vetting:merch.experience.activity_title_label'
											)}
											type={'text'}
											{...register(
												`activities.${index}.title`
											)}
											errorMessage={
												errors?.activities?.[index]
													?.title?.message
											}
										/>
										<CharCounter<ExperienceShellMerchOutputSchema>
											isDisabled={isOriginalContent}
											maxLength={
												EXPERIENCE_OUTPUT_CHARACTER_LIMITS
													.ACTIVITY.TITLE
											}
											name={`activities.${index}.title`}
										/>
									</div>

									<div className='flex w-full flex-col'>
										<TextArea
											disabled={isReadOnly}
											errorMessage={
												errors?.activities?.[index]
													?.description?.message
											}
											label={t(
												'vetting:merch.experience.activity_description_label'
											)}
											rows={3}
											{...register(
												`activities.${index}.description`
											)}
										/>
										<CharCounter<ExperienceShellMerchOutputSchema>
											isDisabled={isOriginalContent}
											maxLength={
												EXPERIENCE_OUTPUT_CHARACTER_LIMITS
													.ACTIVITY.DESCRIPTION
											}
											name={`activities.${index}.description`}
										/>
									</div>
								</div>
							))}
							{isTraining &&
								isOriginalContent &&
								fields.length < 10 && (
									<ButtonLegacy
										className='self-start'
										onClick={() =>
											append({
												title: '',
												description: ''
											})
										}
										size='sm'
										type='button'
										variant='tertiary'
									>
										<PlusIcon />
										{t(
											'vetting:merch.experience.add_activity_label'
										)}
									</ButtonLegacy>
								)}
						</div>

						<Separator orientation='horizontal' />

						<div className='flex w-full flex-col gap-4 py-6 text-neutral-900'>
							<h4 className='font-medium text-base'>
								{t('common:host')}
							</h4>
							<div className='flex w-full flex-col'>
								<InputText
									disabled={isReadOnly}
									label={t(
										'vetting:merch.experience.expertise__tile_label'
									)}
									type={'text'}
									{...register('host_experience_title')}
									errorMessage={
										errors?.host_experience_title?.message
									}
								/>
								<CharCounter<ExperienceShellMerchOutputSchema>
									isDisabled={isOriginalContent}
									maxLength={
										EXPERIENCE_OUTPUT_CHARACTER_LIMITS.HOST
											.TITLE
									}
									name='host_experience_title'
								/>
							</div>

							<div className='flex w-full flex-col'>
								<TextArea
									disabled={isReadOnly}
									label={t(
										'vetting:merch.experience.expertise_label'
									)}
									rows={3}
									{...register('host_expertise_description')}
									errorMessage={
										errors?.host_expertise_description
											?.message
									}
								/>
								<CharCounter<ExperienceShellMerchOutputSchema>
									isDisabled={isOriginalContent}
									maxLength={
										EXPERIENCE_OUTPUT_CHARACTER_LIMITS.HOST
											.BIO
									}
									name='host_expertise_description'
								/>
							</div>

							<div className='flex w-full flex-col'>
								<InputText
									disabled={isReadOnly}
									label={t(
										'vetting:merch.experience.recognition_title_label'
									)}
									type={'text'}
									{...register('host_recognition_title')}
									errorMessage={
										errors?.host_recognition_title?.message
									}
								/>
								<CharCounter<ExperienceShellMerchOutputSchema>
									isDisabled={isOriginalContent}
									maxLength={
										EXPERIENCE_OUTPUT_CHARACTER_LIMITS.HOST
											.RECOGNITION_TITLE
									}
									name='host_recognition_title'
								/>
							</div>
						</div>
					</div>
				)}
				<button
					ref={finalSubmitRef}
					style={{ display: 'none' }}
					type='submit'
				/>
			</form>
		</FormProvider>
	)
}
